import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Stack,
  Container,
  Typography,
  Checkbox,
  FormControlLabel,
  useTheme,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import HorizontalBlade, {
  StyledFeatureTypography,
} from "../../components/blades/horizontalblade.js";
import GoogleButton from "react-google-button";

// Custom hook for registration flow
function useRegistrationFlow() {
  const navigate = useNavigate();
  const {
    googleAuthHandler,
    currentUser,
    userObjectId,
    loading,
    backendDataLoading,
  } = useAuth();
  const [state, setState] = useState({
    status: "idle", // 'idle' | 'loading' | 'success' | 'error'
    error: null,
    termsAccepted: false,
    redirectTriggered: false, // Add flag to prevent multiple redirects
  });



  // Get pending claim token
  const pendingClaimToken = sessionStorage.getItem("pendingClaimToken");

  // Handle successful registration - Simplified approach
  // Since we're now handling redirect directly in the register function,
  // we can remove the complex useEffect logic
  useEffect(() => {
    // This useEffect is now mainly for cleanup and edge cases
    if (state.redirectTriggered) return;

    // Only redirect here if something went wrong with the direct redirect
    if (currentUser && state.status === "success" && !loading) {
      console.log("Fallback redirect triggered");

      const timeoutId = setTimeout(() => {
        if (!state.redirectTriggered) {
          setState((prev) => ({ ...prev, redirectTriggered: true }));

          const redirectTo = pendingClaimToken
            ? `/claim/${pendingClaimToken}`
            : "/loggedinhome";

          if (pendingClaimToken) {
            sessionStorage.removeItem("pendingClaimToken");
          }

          // Use window.location for fallback redirect too
          window.location.href = redirectTo;
        }
      }, 3000); // 3 second fallback

      return () => clearTimeout(timeoutId);
    }
  }, [
    currentUser,
    loading,
    state.status,
    state.redirectTriggered,
    pendingClaimToken,
    navigate,
  ]);

  const actions = {
    toggleTerms: () =>
      setState((prev) => ({
        ...prev,
        termsAccepted: !prev.termsAccepted,
        error: null,
      })),

    register: async () => {
      if (!state.termsAccepted) {
        setState((prev) => ({
          ...prev,
          error: "Please agree to the terms and conditions before proceeding.",
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        status: "loading",
        error: null,
        redirectTriggered: false, // Reset redirect flag
      }));

      try {
        console.log("Starting Google registration...");
        const result = await googleAuthHandler(false); // false for registration

        if (result) {
          console.log("Google registration initiated successfully");
          setState((prev) => ({ ...prev, status: "success" }));

          // Give a moment for the auth state to settle, then redirect
          // We don't need to wait for userObjectId since registration was successful
          setTimeout(() => {
            console.log("Registration complete, triggering redirect");
            setState((prev) => ({ ...prev, redirectTriggered: true }));

            const redirectTo = pendingClaimToken
              ? `/claim/${pendingClaimToken}`
              : "/loggedinhome";

            console.log(`Redirecting to: ${redirectTo}`);

            // Clean up token
            if (pendingClaimToken) {
              sessionStorage.removeItem("pendingClaimToken");
            }

            // Use window.location for a hard redirect to ensure page actually changes
            window.location.href = redirectTo;
          }, 1500); // 1.5 second delay to ensure auth state is settled
        } else {
          console.log("Registration failed - no result returned");
          setState((prev) => ({
            ...prev,
            status: "error",
            error: "Registration failed - please try again",
          }));
        }
      } catch (error) {
        console.error("Registration failed:", error);
        setState((prev) => ({
          ...prev,
          status: "error",
          error: error.message || "Failed to register with Google",
        }));
      }
    },

    clearError: () => setState((prev) => ({ ...prev, error: null })),
  };

  return { state, actions, pendingClaimToken };
}

// Pure component for registration form
function RegistrationForm({ state, actions, pendingClaimToken, theme }) {
  const { termsAccepted } = state;
  const isButtonDisabled = !termsAccepted || state.status === "loading";

  

  return (
    <Stack
      spacing={2}
      sx={{
        width: "100%",
        maxWidth: "400px",
        margin: "0 auto",
        padding: theme.spacing(3),
      }}
    >
      <StyledFeatureTypography
        sx={{
          mb: 4,
          textAlign: "center",
          color: theme.palette.text.primary,
        }}
      >
        Register with Single Sign-On
      </StyledFeatureTypography>

      <TermsCheckbox
        checked={termsAccepted}
        onChange={actions.toggleTerms}
        theme={theme}
      />

      <GoogleButton
        onClick={actions.register}
        disabled={isButtonDisabled}
        style={{
          opacity: isButtonDisabled ? 0.6 : 1,
          margin: "0 auto",
          display: "flex",
          justifyContent: "center",
        }}
      />

      <StatusMessages
        status={state.status}
        pendingClaimToken={pendingClaimToken}
      />

      <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
        Already have an account? <Link to="/login">Login</Link>
      </Typography>
    </Stack>
  );
}

// Pure component for terms checkbox
function TermsCheckbox({ checked, onChange, theme }) {
  return (
    <FormControlLabel
      control={
        <Checkbox
          sx={{
            color: theme.palette.text.primary,
            "&.Mui-checked": {
              color: theme.palette.text.primary,
            },
          }}
          checked={checked}
          onChange={onChange}
        />
      }
      label={
        <Typography sx={{ color: theme.palette.text.primary }}>
          I agree to the terms and conditions of mydivingsites.com as well as
          those of my SSO choice. I understand that I am responsible for my own
          actions and safety and I will not hold mydivingsites.com, its
          affiliates, or any of its members liable for any damages or injuries
          that may occur as a result of my use of this site.
        </Typography>
      }
    />
  );
}

// Pure component for status messages
function StatusMessages({ status, pendingClaimToken }) {
  if (status === "loading") {
    return (
      <Typography variant="body2" sx={{ textAlign: "center" }}>
        Creating your account, please wait...
      </Typography>
    );
  }

  if (status === "success") {
    return (
      <Typography variant="body2" sx={{ textAlign: "center", color: "green" }}>
        Account created successfully! Redirecting...
      </Typography>
    );
  }

  if (pendingClaimToken) {
    return (
      <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
        After registration, you'll be taken back to claim your dive.
      </Typography>
    );
  }

  return null;
}

// Pure component for error display
function ErrorDisplay({ error }) {
  if (!error) return null;

  return (
    <StyledFeatureTypography
      variant="body1"
      color="error"
      sx={{ mt: 2 }}
      id="registration-error"
      aria-live="polite"
    >
      {error}
    </StyledFeatureTypography>
  );
}

// Main component - purely declarative
export default function Register() {
  const theme = useTheme();
  const { state, actions, pendingClaimToken } = useRegistrationFlow();

  const subtleBackgroundImage1 = "/background-images/reef1.jpg";

  return (
    <Container
      maxWidth="md"
      sx={{
        py: 6,
        px: 4,
        backgroundImage: `url(${subtleBackgroundImage1})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
      }}
    >
      <HorizontalBlade
        style={{
          opacity: 0.85,
          backgroundSize: "fill",
          backgroundPosition: "center",
          padding: "24px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <StyledFeatureTypography
          variant="h3"
          component="h1"
          sx={{ mb: 4, textAlign: "center", color: theme.palette.text.primary }}
        >
          Join the mydivingsites.com Community and Register With Us!!
        </StyledFeatureTypography>
      </HorizontalBlade>

      <HorizontalBlade
        style={{
          opacity: 0.85,
          backgroundSize: "fill",
          backgroundPosition: "center",
          padding: "24px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <RegistrationForm
          state={state}
          actions={actions}
          pendingClaimToken={pendingClaimToken}
          theme={theme}
        />
      </HorizontalBlade>

      <ErrorDisplay error={state.error} />
    </Container>
  );
}
